/**
 * Admin > Reports > User-wise + Algo-wise Monthly P&L Report (V1).
 *
 * Read-only. Every call goes through `algoPnlReportService`, which hits the
 * ADMIN-gated `/api/v1/admin/reports/algo-pnl*` endpoints (see
 * `app.api.v1.admin_algo_pnl_report` on the backend -- identical role-gating
 * to every other Admin page, e.g. `AnalyticsInsightsPage`).
 *
 * Scope (matches the backend service's own docstring exactly): realized P&L
 * from CLOSED, customer-owned Algo Engine positions only (both LIVE and
 * PAPER execution modes) -- never backtest/simulation results, never
 * unrealized/open-position P&L, never a manual Trade Builder execution.
 *
 * All aggregation (sums, win/loss counts, win rate, monthly + overall
 * totals) is computed on the backend; this page only renders what the API
 * already returns -- it never re-sums `rows` itself.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import algoPnlReportService from "@/services/algoPnlReportService";
import type {
  AlgoPnlReportFilterOptionsResponse,
  AlgoPnlReportMonthlyTotal,
  AlgoPnlReportOverallTotal,
  AlgoPnlReportResponse,
  AlgoPnlReportRow,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";

type RangePreset = "this_month" | "last_month" | "custom";

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function currentMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toDateInputValue(start), end: toDateInputValue(end) };
}

function lastMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { start: toDateInputValue(start), end: toDateInputValue(end) };
}

function monthLabel(month: string): string {
  const [year, m] = month.split("-").map(Number);
  const d = new Date(year, m - 1, 1);
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function formatPnl(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function PnlText({ value }: { value: number }) {
  const color = value > 0 ? "success.main" : value < 0 ? "error.main" : "text.primary";
  return (
    <Typography component="span" sx={{ color, fontWeight: 600 }}>
      {value >= 0 ? "+" : ""}
      {formatPnl(value)}
    </Typography>
  );
}

function TotalsRow({
  label,
  totals,
  emphasis = false,
}: {
  label: string;
  totals: AlgoPnlReportMonthlyTotal | AlgoPnlReportOverallTotal;
  emphasis?: boolean;
}) {
  return (
    <TableRow sx={{ bgcolor: emphasis ? "action.selected" : "action.hover" }}>
      <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
        {label}
      </TableCell>
      <TableCell sx={{ fontWeight: 700 }}>—</TableCell>
      <TableCell align="right">
        <PnlText value={totals.realized_pnl} />
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 700 }}>
        {totals.trade_count}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 700 }}>
        {totals.winning_trades}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 700 }}>
        {totals.losing_trades}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 700 }}>
        {totals.win_rate_percent.toFixed(1)}%
      </TableCell>
    </TableRow>
  );
}

export function AlgoPnlReportPage() {
  const [rangePreset, setRangePreset] = useState<RangePreset>("this_month");
  const [customStart, setCustomStart] = useState(() => currentMonthRange().start);
  const [customEnd, setCustomEnd] = useState(() => currentMonthRange().end);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedAlgoId, setSelectedAlgoId] = useState<string>("");

  const [filterOptions, setFilterOptions] = useState<AlgoPnlReportFilterOptionsResponse | null>(null);
  const [report, setReport] = useState<AlgoPnlReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dateRange = useMemo(() => {
    if (rangePreset === "this_month") return currentMonthRange();
    if (rangePreset === "last_month") return lastMonthRange();
    return { start: customStart, end: customEnd };
  }, [rangePreset, customStart, customEnd]);

  useEffect(() => {
    algoPnlReportService
      .getFilterOptions()
      .then(setFilterOptions)
      .catch((err) => setError(extractErrorMessage(err)));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await algoPnlReportService.getReport({
        user_id: selectedUserId || undefined,
        algo_id: selectedAlgoId || undefined,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      setReport(data);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedUserId, selectedAlgoId, dateRange]);

  useEffect(() => {
    load();
  }, [load]);

  // Group rows by month, in the order the backend already returned them
  // (sorted month, then user, then algo) -- never re-sorted or re-summed
  // here, only grouped for display.
  const rowsByMonth = useMemo(() => {
    const groups = new Map<string, AlgoPnlReportRow[]>();
    for (const row of report?.rows ?? []) {
      const existing = groups.get(row.month);
      if (existing) existing.push(row);
      else groups.set(row.month, [row]);
    }
    return groups;
  }, [report]);

  const monthlyTotalsByMonth = useMemo(() => {
    const map = new Map<string, AlgoPnlReportMonthlyTotal>();
    for (const total of report?.monthly_totals ?? []) {
      map.set(total.month, total);
    }
    return map;
  }, [report]);

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
        User-wise + Algo-wise Monthly P&amp;L Report
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md="auto">
              <ToggleButtonGroup
                size="small"
                value={rangePreset}
                exclusive
                onChange={(_e, value) => {
                  if (value) setRangePreset(value as RangePreset);
                }}
              >
                <ToggleButton value="this_month">This Month</ToggleButton>
                <ToggleButton value="last_month">Last Month</ToggleButton>
                <ToggleButton value="custom">Custom Range</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {rangePreset === "custom" && (
              <>
                <Grid item xs={6} md="auto">
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} md="auto">
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel id="pnl-report-user-filter-label">User</InputLabel>
                <Select
                  labelId="pnl-report-user-filter-label"
                  label="User"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {(filterOptions?.users ?? []).map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel id="pnl-report-algo-filter-label">Algo Model</InputLabel>
                <Select
                  labelId="pnl-report-algo-filter-label"
                  label="Algo Model"
                  value={selectedAlgoId}
                  onChange={(e) => setSelectedAlgoId(e.target.value)}
                >
                  <MenuItem value="">All Algo Models</MenuItem>
                  {(filterOptions?.algos ?? []).map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !report ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Month</TableCell>
                    <TableCell>Algo Model</TableCell>
                    <TableCell align="right">Realized P&amp;L</TableCell>
                    <TableCell align="right"># Trades</TableCell>
                    <TableCell align="right">Winning</TableCell>
                    <TableCell align="right">Losing</TableCell>
                    <TableCell align="right">Win Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsByMonth.size === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No completed trades in this selection.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {Array.from(rowsByMonth.entries()).map(([month, rows]) => (
                    <Fragment key={month}>
                      {rows.map((row) => (
                        <TableRow key={`${row.user_id}-${row.algo_id}-${row.month}`} hover>
                          <TableCell>
                            <Typography variant="body2">{row.user_full_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.user_email}
                            </Typography>
                          </TableCell>
                          <TableCell>{monthLabel(row.month)}</TableCell>
                          <TableCell>{row.algo_name}</TableCell>
                          <TableCell align="right">
                            <PnlText value={row.realized_pnl} />
                          </TableCell>
                          <TableCell align="right">{row.trade_count}</TableCell>
                          <TableCell align="right">{row.winning_trades}</TableCell>
                          <TableCell align="right">{row.losing_trades}</TableCell>
                          <TableCell align="right">{row.win_rate_percent.toFixed(1)}%</TableCell>
                        </TableRow>
                      ))}
                      {monthlyTotalsByMonth.has(month) && (
                        <TotalsRow label={`${monthLabel(month)} Total`} totals={monthlyTotalsByMonth.get(month)!} />
                      )}
                    </Fragment>
                  ))}
                  {report && report.rows.length > 0 && (
                    <TotalsRow label="Overall Total" totals={report.overall_total} emphasis />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default AlgoPnlReportPage;
